/**
 * Live preview runs user-written HTML/CSS/JS inside an iframe via `srcDoc`.
 */

import { Head, router, usePage } from '@inertiajs/react'
import Editor, { loader as monacoLoader } from '@monaco-editor/react'

/** Same-origin Monaco assets (see scripts/copy-monaco.mjs); CSP blocks CDN loader.js. */
monacoLoader.config({ paths: { vs: '/monaco-editor/vs' } })
import type { PointerEvent as ReactPointerEvent } from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import ComponentLibraryPanel from '../../components/sandbox/ComponentLibraryPanel'
import PanelSwitch from '../../components/PanelSwitch'
import {
  getSandboxPresetById,
  SANDBOX_COMPONENT_PRESETS,
  SANDBOX_PRESET_QUERY_PARAM,
  sandboxHrefWithPreset,
  type SandboxComponentPreset,
} from '../../lib/sandboxComponentPresets'
import {
  buildPreviewSrcDoc,
  isSandboxPreviewHighlightLibraryTabMessage,
  SANDBOX_MAX_TOTAL_CHARS,
} from '../../lib/sandboxPreview'
import { useTheme } from '../../hooks/useTheme'

type SourcePanel = 'html' | 'css' | 'js'

const PANEL_META: Record<SourcePanel, { file: string; icon: string; language: 'html' | 'css' | 'javascript' }> = {
  html: { file: 'index.html', icon: 'html', language: 'html' },
  css: { file: 'styles.css', icon: 'css', language: 'css' },
  js: { file: 'main.js', icon: 'javascript', language: 'javascript' },
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(id)
  }, [value, delayMs])
  return debounced
}

function nowTs(): string {
  return new Date().toLocaleTimeString('en-GB', { hour12: false })
}

/** Initial editors match the `starter` library preset (including HTML/CSS/JS comments in source). */
function starterSandboxPreset(): SandboxComponentPreset {
  const p = getSandboxPresetById('starter')
  if (!p) throw new Error('Sandbox starter preset is missing from registry.')
  return p
}

const STARTER_PRESET = starterSandboxPreset()

const SANDBOX_WORKSPACE_SPLIT_PX_KEY = 'nogrok:sandbox-workspace-split-px'
const SANDBOX_WORKSPACE_GUTTER_PX = 8
const SANDBOX_WORKSPACE_MIN_LEFT_PX = 260
const SANDBOX_WORKSPACE_MIN_RIGHT_PX = 260

export default function Sandbox() {
  const { url } = usePage()
  const { theme: appTheme } = useTheme()
  const monacoTheme = appTheme === 'light' ? 'vs' : 'vs-dark'

  const [minimized, setMinimized] = useState<Record<SourcePanel, boolean>>({
    html: false,
    css: false,
    js: false,
  })
  const [html, setHtml] = useState(STARTER_PRESET.html)
  const [css, setCss] = useState(STARTER_PRESET.css)
  const [js, setJs] = useState(STARTER_PRESET.js)
  /** When auto-recompile is off, the iframe uses this snapshot until you click Update preview. */
  const [committedBundle, setCommittedBundle] = useState(() => ({
    html: STARTER_PRESET.html,
    css: STARTER_PRESET.css,
    js: STARTER_PRESET.js,
  }))
  const [autoRecompile, setAutoRecompile] = useState(true)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [libraryTabFlash, setLibraryTabFlash] = useState(false)
  const previewFrameRef = useRef<HTMLIFrameElement>(null)
  const [strictMode, setStrictMode] = useState(false)
  const [previewKey, setPreviewKey] = useState(0)
  const [consoleLines, setConsoleLines] = useState(() => [
    `[${nowTs()}] Initializing sandbox environment…`,
    `[${nowTs()}] Mounting live preview (iframe / srcDoc).`,
  ])

  const bundle = useMemo(() => ({ html, css, js }), [html, css, js])
  const debounced = useDebouncedValue(bundle, 140)
  const previewBundle = autoRecompile ? debounced : committedBundle

  const srcDoc = useMemo(() => {
    const { html: h, css: c, js: j } = previewBundle
    const scriptBody = strictMode ? `'use strict';\n${j}` : j
    return buildPreviewSrcDoc(h, c, scriptBody, { theme: appTheme })
  }, [previewBundle, strictMode, appTheme])

  const digestRef = useRef<string>('')
  useEffect(() => {
    const sig = `${previewBundle.html.length}:${previewBundle.css.length}:${previewBundle.js.length}:${strictMode}`
    if (sig === digestRef.current) return
    digestRef.current = sig
    const n = previewBundle.html.length + previewBundle.css.length + previewBundle.js.length
    setConsoleLines((prev) => [
      ...prev.slice(-12),
      `[${nowTs()}] Preview synced: ${n} chars${strictMode ? " (JS: 'use strict')" : ''}.`,
    ])
  }, [previewBundle, strictMode])

  const toggleMinimized = useCallback((id: SourcePanel) => {
    setMinimized((m) => ({ ...m, [id]: !m[id] }))
  }, [])

  const pushLog = useCallback((line: string) => {
    setConsoleLines((prev) => [...prev.slice(-12), `[${nowTs()}] ${line}`])
  }, [])

  const clearConsole = useCallback(() => {
    setConsoleLines([`[${nowTs()}] Console cleared.`])
  }, [])

  const applyPreviewFromEditors = useCallback(() => {
    setCommittedBundle({ html, css, js })
    pushLog('Preview updated from editors.')
  }, [html, css, js, pushLog])

  const redeploy = useCallback(() => {
    setPreviewKey((k) => k + 1)
    pushLog('Hard reload: preview frame remounted.')
  }, [pushLog])

  const copyExport = useCallback(async () => {
    const { html: h, css: c, js: j } = previewBundle
    const n = h.length + c.length + j.length
    if (n > SANDBOX_MAX_TOTAL_CHARS) {
      pushLog('Copy skipped: source over preview size limit; shorten before export.')
      return
    }
    try {
      await navigator.clipboard.writeText(srcDoc)
      pushLog('Copied full srcDoc HTML to clipboard.')
    } catch {
      pushLog('ERROR: clipboard copy failed.')
    }
  }, [previewBundle, srcDoc, pushLog])

  const loadPreset = useCallback(
    (preset: SandboxComponentPreset) => {
      /* Match URL → useEffect’s ?preset= branch so a refresh loads this example, not the old query. */
      urlPresetParamRef.current = preset.id
      setHtml(preset.html)
      setCss(preset.css)
      setJs(preset.js)
      setCommittedBundle({ html: preset.html, css: preset.css, js: preset.js })
      pushLog(`Loaded preset: ${preset.title}.`)
      setLibraryOpen(false)
      router.visit(sandboxHrefWithPreset(preset.id), {
        replace: true,
        preserveState: true,
        preserveScroll: true,
      })
    },
    [pushLog],
  )

  const urlPresetParamRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const q = url.includes('?') ? url.slice(url.indexOf('?') + 1) : ''
    const id = new URLSearchParams(q).get(SANDBOX_PRESET_QUERY_PARAM)?.trim() || undefined
    if (!id) {
      urlPresetParamRef.current = undefined
      return
    }
    if (urlPresetParamRef.current === id) return
    urlPresetParamRef.current = id
    const preset = getSandboxPresetById(id)
    if (!preset) {
      pushLog(`Unknown preset in URL: ${id}.`)
      return
    }
    setHtml(preset.html)
    setCss(preset.css)
    setJs(preset.js)
    setCommittedBundle({ html: preset.html, css: preset.css, js: preset.js })
    pushLog(`Loaded preset from URL: ${preset.title}.`)
  }, [url, pushLog])

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== previewFrameRef.current?.contentWindow) return
      if (!isSandboxPreviewHighlightLibraryTabMessage(e.data)) return
      /* Preview iframe is sandboxed without allow-same-origin → opaque origin; event.origin is "null". */
      if (e.origin !== window.location.origin && e.origin !== 'null') return
      setLibraryTabFlash(true)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const editorWellRef = useRef<HTMLDivElement>(null)
  const consolePanelRef = useRef<HTMLDivElement>(null)
  const workspaceGridRef = useRef<HTMLDivElement>(null)
  const workspaceLeftRef = useRef<HTMLDivElement>(null)
  const workspaceSplitDragRef = useRef({ active: false, startX: 0, startLeftPx: 0 })
  const workspaceLastSplitPxRef = useRef<number | null>(null)
  const [workspaceSplitLeftPx, setWorkspaceSplitLeftPx] = useState<number | null>(null)
  const libraryPanelRef = useRef<HTMLDivElement>(null)
  const libraryTabButtonRef = useRef<HTMLButtonElement>(null)
  const [libraryDock, setLibraryDock] = useState({ top: 0, height: 0 })

  const updateLibraryDock = useCallback(() => {
    const editors = editorWellRef.current?.getBoundingClientRect()
    const consoleBox = consolePanelRef.current?.getBoundingClientRect()
    if (!editors && !consoleBox) return
    const top = Math.min(editors?.top ?? Infinity, consoleBox?.top ?? Infinity)
    const bottom = Math.max(editors?.bottom ?? 0, consoleBox?.bottom ?? 0)
    if (!Number.isFinite(top)) return
    const height = Math.max(0, Math.min(bottom - top, window.innerHeight - top))
    setLibraryDock({ top, height })
  }, [])

  useLayoutEffect(() => {
    updateLibraryDock()

    const well = editorWellRef.current
    const consoleEl = consolePanelRef.current
    const tabBtn = libraryTabButtonRef.current

    const syncLibraryTabStackHeight = () => {
      const panel = libraryPanelRef.current
      const btn = libraryTabButtonRef.current
      if (!panel || !btn) return
      const tabControlH = btn.getBoundingClientRect().height
      const panelH = panel.clientHeight
      /* Top inset for panel ::after — height of the tab *control*, not the full tab column. */
      const topPx = Math.max(0, Math.min(tabControlH, panelH))
      panel.style.setProperty('--sandbox-library-tab-stack-height', `${topPx}px`)
    }

    syncLibraryTabStackHeight()

    const roWell = well
      ? new ResizeObserver(() => {
          updateLibraryDock()
          syncLibraryTabStackHeight()
        })
      : null
    if (well && roWell) roWell.observe(well)
    if (consoleEl && roWell) roWell.observe(consoleEl)

    const roTabBtn = tabBtn ? new ResizeObserver(syncLibraryTabStackHeight) : null
    if (tabBtn && roTabBtn) roTabBtn.observe(tabBtn)

    window.addEventListener('resize', updateLibraryDock)
    window.addEventListener('scroll', updateLibraryDock, true)

    return () => {
      roWell?.disconnect()
      roTabBtn?.disconnect()
      window.removeEventListener('resize', updateLibraryDock)
      window.removeEventListener('scroll', updateLibraryDock, true)
    }
  }, [updateLibraryDock, libraryDock.height, libraryDock.top, libraryOpen])

  useEffect(() => {
    if (!libraryOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [libraryOpen])

  useEffect(() => {
    if (!libraryOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLibraryOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [libraryOpen])

  const clampWorkspaceSplit = useCallback((px: number, gridInnerWidth: number) => {
    const maxLeft = Math.max(
      0,
      gridInnerWidth - SANDBOX_WORKSPACE_MIN_RIGHT_PX - SANDBOX_WORKSPACE_GUTTER_PX,
    )
    /* When the grid is narrower than min-left + gutter + min-right, cap min-left so the right track stays > 0. */
    const minLeft = Math.min(SANDBOX_WORKSPACE_MIN_LEFT_PX, maxLeft)
    const safePx = Number.isFinite(px) ? px : minLeft
    return Math.max(minLeft, Math.min(maxLeft, safePx))
  }, [])

  useLayoutEffect(() => {
    const grid = workspaceGridRef.current
    if (!grid) return

    const sync = () => {
      if (!window.matchMedia('(min-width: 1024px)').matches) return
      const w = grid.getBoundingClientRect().width
      if (w < 32) return

      setWorkspaceSplitLeftPx((prev) => {
        let base = prev
        if (base == null) {
          const raw = window.localStorage.getItem(SANDBOX_WORKSPACE_SPLIT_PX_KEY)
          const stored = raw ? Number.parseInt(raw, 10) : NaN
          base = Number.isFinite(stored) ? stored : Math.round(w * 0.58)
        }
        const clamped = clampWorkspaceSplit(base, w)
        workspaceLastSplitPxRef.current = clamped
        return clamped
      })
    }

    sync()
    const ro = new ResizeObserver(sync)
    ro.observe(grid)
    window.addEventListener('resize', sync)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', sync)
    }
  }, [clampWorkspaceSplit])

  const persistWorkspaceSplit = useCallback((px: number) => {
    try {
      window.localStorage.setItem(SANDBOX_WORKSPACE_SPLIT_PX_KEY, String(Math.round(px)))
    } catch {
      /* private mode or quota */
    }
  }, [])

  const onWorkspaceGutterPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      const grid = workspaceGridRef.current
      const left = workspaceLeftRef.current
      if (!grid || !left) return
      e.preventDefault()
      workspaceSplitDragRef.current = {
        active: true,
        startX: e.clientX,
        startLeftPx: left.getBoundingClientRect().width,
      }
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [],
  )

  const onWorkspaceGutterPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!workspaceSplitDragRef.current.active) return
      const grid = workspaceGridRef.current
      if (!grid) return
      const w = grid.getBoundingClientRect().width
      const dx = e.clientX - workspaceSplitDragRef.current.startX
      const next = clampWorkspaceSplit(workspaceSplitDragRef.current.startLeftPx + dx, w)
      workspaceLastSplitPxRef.current = next
      setWorkspaceSplitLeftPx(next)
    },
    [clampWorkspaceSplit],
  )

  const endWorkspaceSplitDrag = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const wasActive = workspaceSplitDragRef.current.active
      workspaceSplitDragRef.current.active = false
      try {
        e.currentTarget.releasePointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
      if (wasActive) {
        const px = workspaceLastSplitPxRef.current
        if (px != null && Number.isFinite(px)) {
          persistWorkspaceSplit(px)
        }
      }
    },
    [persistWorkspaceSplit],
  )

  return (
    <>
      <Head title="Frontend sandbox" />

      {libraryOpen ? (
        <button
          type="button"
          aria-label="Close component library"
          className="fixed inset-0 z-30 bg-black/45 backdrop-blur-[1px] motion-safe:transition-opacity"
          onClick={() => setLibraryOpen(false)}
        />
      ) : null}

      {/* Sliding shell stays transparent so the closed peek is only the tab (no full-height strip). */}
      <div
        className="pointer-events-none fixed left-0 z-[35]"
        style={{
          top: libraryDock.height > 0 ? libraryDock.top : undefined,
          height: libraryDock.height > 0 ? libraryDock.height : undefined,
          visibility: libraryDock.height > 0 ? 'visible' : 'hidden',
        }}
      >
        <div
          className="pointer-events-auto flex h-full w-max flex-row items-start overflow-visible bg-transparent transition-transform duration-300 ease-out motion-reduce:transition-none"
          style={{
            /* Peek is exactly the tab column (w-10 = 2.5rem). */
            transform: libraryOpen ? 'translateX(0)' : 'translateX(calc(2.5rem - 100%))',
          }}
        >
          <div
            ref={libraryPanelRef}
            id="sandbox-component-library-panel"
            role="dialog"
            aria-modal={libraryOpen}
            aria-labelledby="sandbox-component-library-heading"
            aria-hidden={!libraryOpen}
            className="sandbox-library-panel flex h-full min-h-0 w-[min(1200px,calc(100vw-3.5rem))] shrink-0 flex-col overflow-hidden rounded-l-xl rounded-br-xl bg-surface-container-low"
          >
            <ComponentLibraryPanel
              presets={SANDBOX_COMPONENT_PRESETS}
              strictMode={strictMode}
              onLoad={loadPreset}
              variant="drawer"
              drawerOpen={libraryOpen}
            />
          </div>
          <button
            ref={libraryTabButtonRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setLibraryOpen((o) => !o)
            }}
            aria-expanded={libraryOpen}
            aria-controls="sandbox-component-library-panel"
            aria-label={libraryOpen ? 'Close component library' : 'Open component library'}
            title={libraryOpen ? 'Hide component library' : 'Show component library'}
            className={`sandbox-library-tab shrink-0${libraryTabFlash ? ' sandbox-library-tab--flash' : ''}`}
            onAnimationEnd={(ev) => {
              if (ev.animationName === 'sandbox-library-tab-flash') setLibraryTabFlash(false)
            }}
          >
            <span className="sandbox-library-tab-label">library</span>
          </button>
        </div>
      </div>

      <div
        ref={workspaceGridRef}
        className={
          'flex min-h-0 w-full min-w-0 flex-1 flex-col gap-4 lg:grid lg:min-h-[calc(100dvh-9.5rem)] lg:max-h-[calc(100dvh-6.5rem)] lg:grid-rows-[minmax(0,1fr)] lg:gap-x-0 lg:gap-y-4 ' +
          (workspaceSplitLeftPx == null || !Number.isFinite(workspaceSplitLeftPx)
            ? 'lg:grid-cols-[minmax(0,1fr)_8px_minmax(0,1fr)]'
            : '')
        }
        style={
          workspaceSplitLeftPx != null && Number.isFinite(workspaceSplitLeftPx)
            ? {
                gridTemplateColumns: `${workspaceSplitLeftPx}px ${SANDBOX_WORKSPACE_GUTTER_PX}px minmax(0, 1fr)`,
              }
            : undefined
        }
      >
        <div
          ref={workspaceLeftRef}
          className="order-1 flex min-h-0 min-w-0 flex-col gap-4 lg:order-none lg:col-start-1 lg:row-start-1 lg:min-h-0"
        >
          <div
            ref={editorWellRef}
            className="sandbox-code-well flex min-h-0 min-w-0 flex-col gap-2 overflow-hidden rounded-lg p-2 lg:flex-1 lg:min-h-0 lg:p-0"
          >
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
            {(['html', 'css', 'js'] as const).map((id) => {
              const meta = PANEL_META[id]
              const collapsed = minimized[id]
              const value = id === 'html' ? html : id === 'css' ? css : js
              const onChange = (next: string | undefined) => {
                const v = next ?? ''
                if (id === 'html') setHtml(v)
                else if (id === 'css') setCss(v)
                else setJs(v)
              }
                return (
                  <section
                    key={id}
                    className={`overflow-hidden rounded-lg border border-outline-variant/20 bg-surface-container-low ${
                      collapsed
                        ? 'shrink-0'
                        : 'grid min-h-0 flex-1 grid-rows-[auto_minmax(240px,1fr)]'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleMinimized(id)}
                      className="flex h-10 w-full shrink-0 items-center gap-2 border-b border-outline-variant/10 bg-surface-container-highest/50 px-3 text-left transition-colors hover:bg-surface-container-highest"
                      aria-expanded={!collapsed}
                      aria-controls={`sandbox-editor-${id}`}
                    >
                      <span className="material-symbols-outlined text-on-surface-variant">{collapsed ? 'chevron_right' : 'expand_more'}</span>
                      <span className="material-symbols-outlined text-sm text-primary">{meta.icon}</span>
                      <span className="font-mono text-xs text-on-surface/90">{meta.file}</span>
                      <span className="ml-auto font-mono text-[10px] tracking-widest text-on-surface-variant uppercase">
                        {collapsed ? 'Expand' : 'Minimize'}
                      </span>
                    </button>
                    {!collapsed && (
                      <div id={`sandbox-editor-${id}`} className="min-h-0 overflow-hidden">
                        <Editor
                          height="100%"
                          theme={monacoTheme}
                          language={meta.language}
                          value={value}
                          onChange={onChange}
                          options={{
                            minimap: { enabled: false },
                            fontSize: 13,
                            fontFamily: 'IBM Plex Mono, monospace',
                            tabSize: 2,
                            wordWrap: 'on',
                          }}
                        />
                      </div>
                    )}
                  </section>
                )
              })}
            </div>
          </div>

          <div
            ref={consolePanelRef}
            className="flex h-48 max-h-48 min-h-0 shrink-0 flex-col self-start overflow-hidden rounded-lg border border-outline-variant/20 bg-surface-container-lowest p-3 font-mono text-[11px] lg:w-full lg:max-h-48 lg:shrink-0"
          >
          <div className="mb-2 flex shrink-0 items-center gap-3 border-b border-outline-variant/10 pb-1">
            <span className="text-[10px] font-bold tracking-widest text-on-surface-variant uppercase">
              Console_output
            </span>
            <span className="truncate text-on-surface-variant">sandbox-ui</span>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={clearConsole}
                aria-label="Clear console"
                title="Clear console"
                className="flex size-8 shrink-0 items-center justify-center rounded border border-outline-variant/25 text-on-surface-variant transition-colors hover:border-outline-variant/50 hover:bg-surface-container-high hover:text-on-surface"
              >
                <span className="material-symbols-outlined text-[18px]">delete</span>
              </button>
              <span className="hidden sm:inline-flex gap-2">
                <span className="size-1.5 shrink-0 rounded-full bg-secondary" />
                <span className="size-1.5 shrink-0 rounded-full bg-surface-container-highest" />
              </span>
            </div>
          </div>
          <div className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain text-on-surface/80 [scrollbar-gutter:stable]">
            {consoleLines.map((line, i) => (
              <p key={`${i}-${line.slice(0, 24)}`}>{line}</p>
            ))}
          </div>
          </div>
        </div>

        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize editors and preview panels"
          className="hidden shrink-0 cursor-col-resize touch-none select-none bg-transparent transition-colors duration-150 ease-out motion-reduce:transition-none hover:bg-primary-container/35 active:bg-primary-container/35 lg:col-start-2 lg:row-start-1 lg:z-10 lg:flex lg:w-full lg:min-w-0 lg:max-w-none"
          onPointerDown={onWorkspaceGutterPointerDown}
          onPointerMove={onWorkspaceGutterPointerMove}
          onPointerUp={endWorkspaceSplitDrag}
          onPointerCancel={endWorkspaceSplitDrag}
          onLostPointerCapture={() => {
            if (!workspaceSplitDragRef.current.active) return
            workspaceSplitDragRef.current.active = false
            const px = workspaceLastSplitPxRef.current
            if (px != null && Number.isFinite(px)) {
              persistWorkspaceSplit(px)
            }
          }}
        />

        <div className="order-3 flex min-h-[min(48vh,22rem)] min-w-0 w-full flex-1 flex-col gap-4 self-stretch lg:order-none lg:col-start-3 lg:row-start-1 lg:h-full lg:min-h-0 lg:w-full lg:max-w-none lg:self-stretch lg:overflow-hidden">
          <div className="flex min-h-0 min-w-0 flex-1 basis-0 flex-col overflow-hidden rounded-xl border-[12px] border-surface-container-highest bg-surface-container-low sandbox-monitor-bezel lg:h-full lg:min-h-0">
            <div className="flex h-8 shrink-0 items-center justify-between border-b border-outline-variant/30 bg-surface px-4">
              <div className="flex gap-1.5">
                <span className="size-2.5 rounded-full bg-surface-container-highest" />
                <span className="size-2.5 rounded-full bg-surface-container-highest" />
                <span className="size-2.5 rounded-full bg-surface-container-highest" />
              </div>
              <span className="font-mono text-[9px] tracking-widest text-on-surface-variant uppercase">
                srcDoc_preview
              </span>
            </div>
            <div className="relative min-h-0 flex-1 basis-0 overflow-hidden bg-surface-container-lowest p-2">
              {/* No allow-same-origin: same-origin + scripts trivially lifts sandbox. */}
              <iframe
                ref={previewFrameRef}
                key={previewKey}
                title="Preview"
                sandbox="allow-scripts"
                referrerPolicy="no-referrer"
                className="relative z-10 box-border h-full min-h-[12rem] w-full rounded-sm bg-surface-container-low"
                srcDoc={srcDoc}
              />
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.04]"
                style={{
                  backgroundImage:
                    'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
                  backgroundSize: '40px 40px',
                }}
              />
            </div>
          </div>

          <div className="flex shrink-0 flex-col rounded-lg border border-outline-variant/20 bg-surface-container p-3 lg:shrink-0">
            <h3 className="mb-2 font-headline text-[10px] font-bold tracking-widest text-on-surface-variant uppercase">
              Preview &amp; build
            </h3>
            <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
              <PanelSwitch
                compact
                label="Auto"
                checked={autoRecompile}
                title="Debounced live rebuild (~140ms). Off: preview frozen until Update."
                onCheckedChange={(next) => {
                  setAutoRecompile(next)
                  if (!next) {
                    setCommittedBundle(debounced)
                  }
                }}
              />
              <PanelSwitch
                compact
                label="Strict"
                checked={strictMode}
                title={"Prefix preview JS with 'use strict'"}
                onCheckedChange={setStrictMode}
              />
            </div>
            {!autoRecompile ? (
              <button
                type="button"
                onClick={applyPreviewFromEditors}
                className="w-full rounded border border-primary-container/50 bg-primary-container/15 py-1.5 font-headline text-[10px] font-bold tracking-wide uppercase text-primary-container transition-all hover:bg-primary-container/25 active:translate-y-0.5"
              >
                Update preview
              </button>
            ) : null}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={redeploy}
                className="rounded border border-outline-variant/30 bg-surface-container-highest py-1.5 font-headline text-[9px] font-bold tracking-wide uppercase transition-all hover:bg-surface-container-high active:translate-y-0.5"
              >
                Remount
              </button>
              <button
                type="button"
                onClick={copyExport}
                className="rounded border border-outline-variant/30 bg-surface-container-low py-1.5 font-headline text-[9px] font-bold tracking-wide text-on-surface uppercase transition-all hover:bg-surface-container-lowest active:translate-y-0.5"
              >
                Copy HTML
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    </>
  )
}
